/**
 * Portrait — the one way a person or a brand appears on the desk: their photo
 * as a small print with a paper border, or their initials on manila when there
 * is no photo. Replaces the navy circle (Brand Scout), the slate square
 * (sponsor rows), the gradient placeholder (Profile) and Pitch Writer's local
 * print, which this is lifted from.
 *
 * A brand's logo is a mark, not a face: `mark` shows it whole on white and
 * falls back to a single initial. A broken image degrades to the initials —
 * never a blank tile, never another brand's mark.
 */
import React, { useState } from 'react';
import { proxyImageUrl } from '../../utils/imageProxy';

export interface PortraitProps {
  name: string;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** A logo rather than a photo: shown whole on white, one initial when absent. */
  mark?: boolean;
  /** The slight tilt of a print left on the desk (Pitch Writer's "from" tile). */
  tilt?: boolean;
  className?: string;
  title?: string;
  /** The photo failed (or was a 16px favicon): the initials show; the caller may fetch a fresh one. */
  onFailed?: () => void;
}

export function initialsFor(name: string, mark = false): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (mark ? parts.slice(0, 1) : parts.slice(0, 2)).map(p => p.charAt(0)).join('').toUpperCase();
}

const Portrait: React.FC<PortraitProps> = ({ name, src, size = 'md', mark = false, tilt = false, className = '', title, onFailed }) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src) && src !== failedSrc;
  const fail = () => { setFailedSrc(src as string); onFailed?.(); };
  const cls = ['br-portrait', size !== 'md' && `br-portrait--${size}`, mark && 'br-portrait--mark', tilt && 'br-portrait--tilt', className]
    .filter(Boolean).join(' ');
  return (
    <span className={cls} aria-hidden="true" title={title || name}>
      {initialsFor(name, mark)}
      {showImage && <img src={proxyImageUrl(src as string, name)} alt="" loading="lazy" onError={fail}
        // Google's favicon service answers an unknown host with a 16px generic globe; scaled up it looks
        // like a mark but is not the brand's, so anything that small falls back to the initials
        onLoad={e => { if (e.currentTarget.naturalWidth <= 16) fail(); }} />}
    </span>
  );
};

export default Portrait;
